// Brief Prospection Phase 1 §1+§2 — Page événement publique /@{handle}/e/{slug}.
// Expert mis en avant. Flux invité : réservation par email seul (événements
// gratuits) → magic link. Utilisateurs connectés : booking direct.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Users, Star, Mail, Check, Share2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SharePanel from '../components/SharePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtFull = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    + ' à ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};

export default function PublicEventSlugPage() {
  const { handle: rawHandle, slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const handle = (rawHandle || '').replace(/^@/, '');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Guest form
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [guestSent, setGuestSent] = useState(false);
  // Logged-in booking
  const [booked, setBooked] = useState(false);
  const trackedRef = React.useRef(false);

  // Brief Prospection Phase 2 — canal d'attribution (query ?ref ou session)
  const getRef = () => {
    const qref = new URLSearchParams(window.location.search).get('ref');
    return qref || sessionStorage.getItem('os_ref') || null;
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/public/e/${handle}/${slug}`)
      .then((r) => {
        if (!alive) return;
        setEvent(r.data);
        document.title = `${r.data.title} — avec ${r.data.expert?.display_name || 'un expert'}`;
        if (!trackedRef.current) {
          trackedRef.current = true;
          const qref = new URLSearchParams(window.location.search).get('ref');
          if (qref) sessionStorage.setItem('os_ref', qref);
          axios.post(`${API}/public/track`, {
            kind: 'event', handle, event_id: r.data.event_id,
            ref: qref || sessionStorage.getItem('os_ref') || null,
          }).catch(() => {});
        }
      })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [handle, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="animate-spin h-8 w-8 border-3 border-[#7F77DD] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8] p-6 text-center" data-testid="public-event-notfound">
        <p className="text-2xl font-bold text-gray-900">Événement introuvable</p>
        <p className="text-gray-500 mt-2">Ce lien ne correspond à aucune session publiée.</p>
        <Link to="/" className="mt-6 text-[#7F77DD] font-semibold">Découvrir OpenSapiens →</Link>
      </div>
    );
  }

  const expert = event.expert || {};
  const isOwner = user?.user_id === expert.user_id;
  const isFree = !!event.effective_is_free;
  const isFull = (event.capacity_left ?? 1) <= 0;
  const origin = window.location.origin;
  const ogUrl = `${origin}/api/@${expert.handle}/e/${event.slug}`;
  const prettyUrl = `${origin}/@${expert.handle}/e/${event.slug}`;

  const submitGuest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/events/${event.event_id}/guest-booking`, {
        email: email.trim(),
        first_name: firstName.trim() || null,
        ref: getRef(),
      });
      setGuestSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Réservation impossible'));
    } finally {
      setSubmitting(false);
    }
  };

  const bookLoggedIn = async () => {
    setSubmitting(true);
    try {
      const ref = getRef();
      if (!isFree && expert.stripe_charges_enabled) {
        const { data } = await axios.post(
          `${API}/bookings/${event.event_id}/checkout${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`,
          {}, { withCredentials: true });
        if (data.url) { window.location.href = data.url; return; }
      }
      await axios.post(`${API}/calendar/events/${event.event_id}/book`, { ref }, { withCredentials: true });
      setBooked(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Réservation impossible'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBookingBox = () => {
    if (event.is_past) {
      return (
        <div className="p-5 rounded-2xl bg-gray-100 text-center" data-testid="public-event-past">
          <p className="font-semibold text-gray-600">Cet événement est terminé</p>
          <Link to={`/@${expert.handle}`} className="text-sm text-[#7F77DD] font-semibold mt-1 inline-block">
            Voir les prochaines sessions de {expert.display_name} →
          </Link>
        </div>
      );
    }
    if (isFull) {
      return (
        <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200 text-center" data-testid="public-event-full">
          <p className="font-semibold text-amber-800">Cette session est complète</p>
          <Link to={`/@${expert.handle}`} className="text-sm text-[#7F77DD] font-semibold mt-1 inline-block">
            Découvrir d'autres sessions →
          </Link>
        </div>
      );
    }
    if (booked) {
      return (
        <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-center" data-testid="public-event-booked">
          <Check className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <p className="font-bold text-emerald-800">Votre place est réservée 🎉</p>
          <p className="text-sm text-emerald-700 mt-1">Vous recevrez un email de confirmation avec le lien visio.</p>
          <button onClick={() => navigate('/app/sessions')} className="mt-3 text-sm font-semibold text-[#7F77DD]" data-testid="public-event-see-sessions">
            Voir mes sessions →
          </button>
        </div>
      );
    }
    if (guestSent) {
      return (
        <div className="p-6 rounded-2xl bg-[#7F77DD]/5 border border-[#7F77DD]/30 text-center" data-testid="public-event-guest-sent">
          <Mail className="h-8 w-8 text-[#7F77DD] mx-auto mb-2" />
          <p className="font-bold text-gray-900">Vérifiez votre boîte mail 📩</p>
          <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
            Nous avons envoyé un lien de confirmation à <strong>{email}</strong>.<br />
            Cliquez dessus pour valider votre place (valable 48 h).
          </p>
        </div>
      );
    }
    if (user) {
      // Utilisateur connecté → booking direct
      if (!isFree && !expert.stripe_charges_enabled) {
        return (
          <div className="p-5 rounded-2xl bg-gray-100 text-center" data-testid="public-event-payments-pending">
            <p className="font-semibold text-gray-600">Les paiements de cet expert seront bientôt disponibles.</p>
          </div>
        );
      }
      return (
        <button
          onClick={bookLoggedIn}
          disabled={submitting}
          className="w-full py-3.5 rounded-2xl text-white font-bold text-base shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ backgroundColor: isFree ? '#1D9E75' : '#7F77DD' }}
          data-testid="public-event-book-btn"
        >
          {submitting ? 'Un instant…' : (isFree ? 'Réserver gratuitement' : `Réserver · ${event.effective_price} ${event.currency}`)}
        </button>
      );
    }
    if (!isFree) {
      // Payant + non connecté → compte requis (Phase 1)
      return (
        <div className="space-y-2" data-testid="public-event-account-required">
          <button
            onClick={() => navigate(`/signup?redirect=${encodeURIComponent(`/event/${event.event_id}?book=1`)}`)}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-base shadow-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#7F77DD' }}
            data-testid="public-event-signup-btn"
          >
            Créer un compte pour réserver · {event.effective_price} {event.currency}
          </button>
          <p className="text-center text-xs text-gray-400">
            Déjà membre ?{' '}
            <Link to={`/login?redirect=${encodeURIComponent(`/event/${event.event_id}?book=1`)}`} className="text-[#7F77DD] font-semibold">
              Connectez-vous
            </Link>
          </p>
        </div>
      );
    }
    // Gratuit + non connecté → flux invité (email seul)
    return (
      <form onSubmit={submitGuest} className="space-y-3" data-testid="public-event-guest-form">
        <p className="text-sm font-semibold text-gray-700">Réservez votre place — email suffit, sans mot de passe</p>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.ch"
          className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          data-testid="public-event-guest-email"
        />
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Votre prénom (optionnel)"
          className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          data-testid="public-event-guest-firstname"
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="w-full py-3.5 rounded-2xl text-white font-bold text-base shadow-lg hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ backgroundColor: '#1D9E75' }}
          data-testid="public-event-guest-submit"
        >
          {submitting ? 'Envoi…' : 'Réserver ma place gratuitement'}
        </button>
        <p className="text-center text-xs text-gray-400">
          Vous recevrez un email pour confirmer votre place.{' '}
          <Link to={`/login?redirect=${encodeURIComponent(`/event/${event.event_id}?book=1`)}`} className="text-[#7F77DD] font-semibold">
            J'ai déjà un compte
          </Link>
        </p>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="public-event-page">
      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Expert header */}
        <Link
          to={`/@${expert.handle}`}
          className="inline-flex items-center gap-3 mb-8 group"
          data-testid="public-event-expert-link"
        >
          <img src={expert.avatar} alt={expert.display_name} className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-md" />
          <div>
            <p className="font-bold text-gray-900 group-hover:text-[#7F77DD] transition-colors">{expert.display_name}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              {expert.rating_avg > 0 && (
                <><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {expert.rating_avg} · </>
              )}
              Voir le profil <ArrowRight className="h-3 w-3" />
            </p>
          </div>
        </Link>

        {/* Event card */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-7 sm:p-9">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight" data-testid="public-event-title">
              {event.title}
            </h1>
            {isOwner && (
              <button
                onClick={() => setShareOpen(true)}
                className="p-2.5 rounded-xl text-[#7F77DD] border border-[#7F77DD]/30 hover:bg-[#7F77DD]/5 transition-colors flex-shrink-0"
                data-testid="public-event-share-btn"
                aria-label="Partager"
              >
                <Share2 className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-5 space-y-2.5 text-[15px] text-gray-700">
            <p className="flex items-center gap-2.5" data-testid="public-event-date">
              <Calendar className="h-4.5 w-4.5 text-[#7F77DD]" style={{ height: 18, width: 18 }} />
              <span className="capitalize">{fmtFull(event.start_time)}</span>
            </p>
            <p className="flex items-center gap-2.5">
              <Clock style={{ height: 18, width: 18 }} className="text-[#7F77DD]" />
              {event.duration_minutes} minutes · en ligne
            </p>
            {event.max_capacity > 1 && (
              <p className="flex items-center gap-2.5" data-testid="public-event-capacity">
                <Users style={{ height: 18, width: 18 }} className="text-[#7F77DD]" />
                {event.participants_count || 0} inscrit{(event.participants_count || 0) > 1 ? 's' : ''}
                {event.capacity_left > 0 && ` · ${event.capacity_left} place${event.capacity_left > 1 ? 's' : ''} restante${event.capacity_left > 1 ? 's' : ''}`}
              </p>
            )}
          </div>

          {/* Prix */}
          <div className="mt-6" data-testid="public-event-price">
            <span className={`text-2xl font-extrabold ${isFree ? 'text-emerald-600' : 'text-[#7F77DD]'}`}>
              {isFree ? 'Gratuit' : `${event.effective_price} ${event.currency}`}
            </span>
          </div>

          {event.description && (
            <p className="mt-5 text-[15px] text-gray-600 leading-relaxed whitespace-pre-line" data-testid="public-event-description">
              {event.description}
            </p>
          )}

          <div className="mt-8">{renderBookingBox()}</div>
        </div>

        <footer className="py-8 text-center">
          <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Propulsé par <span className="font-semibold">OpenSapiens</span>
          </Link>
        </footer>
      </div>

      <SharePanel
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Partager cette session"
        shareUrl={ogUrl}
        prettyUrl={prettyUrl}
        defaultMessage={`J'anime « ${event.title} » le ${fmtFull(event.start_time)} — réservez votre place 👇`}
      />
    </div>
  );
}
